// ============================================================================
// MANAGE A GST ENGAGEMENT (G-1 slice 2f)
// ============================================================================
// Editing is a DIFFERENT SHAPE from creating, which is why this is a separate
// modal rather than an edit mode bolted onto GstEngagementModal. Creating picks
// a client, a year, a type and a manager, then several registrations at once.
// Editing touches two settings and then acts on ONE scope row at a time — add,
// correct, withdraw, re-elect cadence — each with its own consequence.
//
// Folding them together would have disabled half the create form's fields and
// put the 694-line working path at risk for no gain.
//
// NOTHING HERE REMOVES AN OBLIGATION. Generation is additive and re-runnable by
// design: widening scope creates what is newly owed, narrowing it strands
// obligations. Those are REPORTED in the reconciliation panel, and some of them
// are filed — a return that was lodged is not unmade by an edit months later.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import type {
  GstEngagement,
  GstEngagementRegistration,
  GstScopeReconciliation,
  GstOutOfScopeObligation,
  GstElectableQuarter,
} from 'api/types/gstEngagement.types';

interface Props {
  isOpen: boolean;
  engagement: GstEngagement | null;
  onClose: () => void;
  onSaved: () => void;
  /** Whether the caller may change anything (the `edit` action). */
  canEdit?: boolean;
}

const DATA_SOURCE_OPTIONS = [
  { value: 'CLIENT_SUPPLIED', label: 'Client supplied' },
  { value: 'CONNECTOR', label: 'Connector' },
  { value: 'MIXED', label: 'Mixed' },
];

const CADENCE_OPTIONS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'QRMP', label: 'QRMP — quarterly returns, monthly payment' },
];

const HSN_OPTIONS = [
  { value: '4', label: '4 digits' },
  { value: '6', label: '6 digits' },
  { value: '8', label: '8 digits' },
];

const fyLabel = (y: number) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;

const formatDate = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** yyyy-MM-dd for a date input, without the UTC shift toISOString() introduces. */
const inputDate = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * The four quarter starts of an Indian financial year, for a cadence election.
 *
 * A LOCAL FALLBACK ONLY, used while the server's answer is in flight or if that
 * call fails. The server decides which quarters are actually electable, because
 * it is the only side that knows what has been filed — so this offers all four
 * rather than guessing, and the POST still refuses a blocked one.
 */
const quarterOptions = (financialYear: number) => [
  { value: `${financialYear}-04-01`, label: `Q1 — from 1 Apr ${financialYear}` },
  { value: `${financialYear}-07-01`, label: `Q2 — from 1 Jul ${financialYear}` },
  { value: `${financialYear}-10-01`, label: `Q3 — from 1 Oct ${financialYear}` },
  { value: `${financialYear + 1}-01-01`, label: `Q4 — from 1 Jan ${financialYear + 1}` },
];

/** Filed is the one verdict nothing can be done about, so it reads differently. */
const verdictTone = (verdict: string) =>
  verdict === 'Filed'
    ? { cls: 'badge badge-active', colour: '#16a34a', label: 'Filed — left as is' }
    : verdict === 'InProgress'
      ? { cls: 'badge badge-inactive', colour: '#d97706', label: 'Started' }
      : { cls: 'badge badge-inactive', colour: 'var(--text-secondary)', label: 'Not started' };

const GstEngagementManageModal: React.FC<Props> = ({
  isOpen, engagement, onClose, onSaved, canEdit = false,
}) => {
  const [registrations, setRegistrations] = useState<GstEngagementRegistration[]>([]);
  const [dataSourceMode, setDataSourceMode] = useState('CLIENT_SUPPLIED');
  const [filingNotes, setFilingNotes] = useState('');
  const [rowVersion, setRowVersion] = useState(0);

  const [reconciliation, setReconciliation] = useState<GstScopeReconciliation | null>(null);
  const [busy, setBusy] = useState(false);

  // Per-row working state, keyed by scope-row id: the row being edited and the
  // values being typed into it.
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [rowHsn, setRowHsn] = useState('4');
  const [rowScopeTo, setRowScopeTo] = useState('');

  const [withdrawTarget, setWithdrawTarget] = useState<GstEngagementRegistration | null>(null);
  const [withdrawDate, setWithdrawDate] = useState('');
  const [withdrawReason, setWithdrawReason] = useState('');

  const [cadenceTarget, setCadenceTarget] = useState<GstEngagementRegistration | null>(null);
  const [cadenceQuarter, setCadenceQuarter] = useState('');
  const [cadenceValue, setCadenceValue] = useState('MONTHLY');

  // Which quarters the SERVER says may be elected from, and why not for the
  // rest. Null until it answers — the dialog falls back to offering all four
  // rather than blocking on the call, since the POST enforces the rule anyway.
  const [electable, setElectable] = useState<GstElectableQuarter[] | null>(null);

  const [cancelTarget, setCancelTarget] = useState<GstOutOfScopeObligation | null>(null);
  const [bulkCancelOpen, setBulkCancelOpen] = useState(false);

  const settingsDirty = useMemo(
    () => !!engagement
      && (dataSourceMode !== engagement.dataSourceMode
          || (filingNotes || '') !== (engagement.filingNotes || '')),
    [engagement, dataSourceMode, filingNotes],
  );

  const load = useCallback(async () => {
    if (!engagement) return;
    setBusy(true);
    try {
      const [fresh, recon] = await Promise.all([
        gstEngagementService.getById(engagement.id),
        gstEngagementService.getScopeReconciliation(engagement.id),
      ]);
      setRegistrations(fresh.registrations ?? []);
      setDataSourceMode(fresh.dataSourceMode);
      setFilingNotes(fresh.filingNotes ?? '');
      setRowVersion(fresh.rowVersion);
      setReconciliation(recon);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not load this GST engagement.',
      );
    } finally {
      setBusy(false);
    }
  }, [engagement]);

  useEffect(() => { if (isOpen) void load(); }, [isOpen, load]);

  const run = useCallback(async (
    action: () => Promise<string | void>, fallback: string,
  ) => {
    setBusy(true);
    try {
      const message = await action();
      if (message) notificationService.toast.success(message);
      await load();
      onSaved();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? fallback);
    } finally {
      setBusy(false);
    }
  }, [load, onSaved]);

  const saveSettings = useCallback(() => {
    if (!engagement) return;
    void run(async () => {
      await gstEngagementService.update(engagement.id, {
        dataSourceMode, filingNotes: filingNotes.trim() || null, rowVersion,
      });
      return 'Settings saved.';
    }, 'Could not save the settings.');
  }, [engagement, dataSourceMode, filingNotes, rowVersion, run]);

  const saveRow = useCallback((row: GstEngagementRegistration) => {
    if (!engagement) return;
    void run(async () => {
      await gstEngagementService.updateRegistration(engagement.id, row.id, {
        requiredHsnDigits: Number(rowHsn),
        scopeFrom: row.scopeFrom ?? null,
        scopeTo: rowScopeTo || null,
        rowVersion: row.rowVersion,
      });
      setEditingRow(null);
      return `${row.gstin} updated.`;
    }, 'Could not update the registration.');
  }, [engagement, rowHsn, rowScopeTo, run]);

  const confirmWithdraw = useCallback(() => {
    if (!engagement || !withdrawTarget) return;
    const target = withdrawTarget;
    void run(async () => {
      const result = await gstEngagementService.withdrawRegistration(
        engagement.id, target.id,
        { scopeTo: withdrawDate, reason: withdrawReason.trim() || null, rowVersion: target.rowVersion });

      setWithdrawTarget(null);
      setWithdrawDate('');
      setWithdrawReason('');

      return result.outOfScope.length === 0
        ? `${target.gstin} withdrawn. No obligations fall outside scope.`
        : `${target.gstin} withdrawn. ${result.outOfScope.length} obligation(s) now fall outside scope — see below.`;
    }, 'Could not withdraw the registration.');
  }, [engagement, withdrawTarget, withdrawDate, withdrawReason, run]);

  /**
   * Asks the server which quarters may be elected from, and preselects the
   * first that may.
   *
   * <p>A failure here is deliberately QUIET: all four quarters stay on offer and
   * the POST refuses a blocked one with the same message. Blocking the dialog on
   * an advisory call would stop a legitimate change over a hiccup.</p>
   */
  const loadElectable = useCallback(async (row: GstEngagementRegistration) => {
    if (!engagement) return;
    setElectable(null);
    try {
      const options = await gstEngagementService.getElectableQuarters(engagement.id, row.id);
      setElectable(options);

      const firstOpen = options.find((q) => q.selectable);
      if (firstOpen) setCadenceQuarter(inputDate(firstOpen.effectiveFromQuarter));
    } catch {
      setElectable(null);
    }
  }, [engagement]);

  const confirmCadence = useCallback(() => {
    if (!engagement || !cadenceTarget) return;
    const target = cadenceTarget;
    void run(async () => {
      const result = await gstEngagementService.addFilingOption(engagement.id, target.id, {
        effectiveFromQuarter: cadenceQuarter,
        filingFrequency: cadenceValue,
        returnTypes: cadenceValue === 'QRMP'
          ? ['GSTR1', 'IFF', 'PMT06', 'GSTR3B']
          : ['GSTR1', 'GSTR3B'],
        notes: null,
      });
      setCadenceTarget(null);
      setElectable(null);

      // "No change" is said plainly rather than dressed up as a save. Telling
      // somebody their switch was recorded when it was not is the failure this
      // whole path had.
      if (!result.changed) {
        return `No change — ${target.gstin} was already ${cadenceValue} from `
          + `${formatDate(cadenceQuarter)}.`;
      }

      const created = result.generation?.created ?? 0;
      const stranded = result.reconciliation?.outOfScope.length ?? 0;

      // BOTH halves are reported. A frequency switch creates the new shape AND
      // leaves the old one behind, and a user who is told only the first will
      // reasonably wonder why the old returns are still on the list.
      return [
        `${target.gstin} set to ${cadenceValue} from ${formatDate(cadenceQuarter)}.`,
        created > 0 ? `${created} return period(s) created.` : '',
        stranded > 0
          ? `${stranded} period(s) from the previous frequency are superseded — see below.`
          : '',
      ].filter(Boolean).join(' ');
    }, 'Could not record the filing frequency.');
  }, [engagement, cadenceTarget, cadenceQuarter, cadenceValue, run]);

  const confirmCancel = useCallback(() => {
    if (!engagement || !cancelTarget) return;
    const target = cancelTarget;
    void run(async () => {
      await gstEngagementService.cancelOutOfScopeObligation(engagement.id, target.obligationId);
      setCancelTarget(null);
      return `${target.formCode} ${target.periodLabel} cancelled.`;
    }, 'Could not cancel the obligation.');
  }, [engagement, cancelTarget, run]);

  const confirmBulkCancel = useCallback(() => {
    if (!engagement) return;
    void run(async () => {
      const result = await gstEngagementService.cancelSupersededObligations(engagement.id);
      setBulkCancelOpen(false);

      // What was LEFT matters as much as what went. A user who asked to clear
      // everything and sees "20 cancelled" needs to know the other four were
      // filed, not that the call half-worked.
      return [
        `${result.cancelled} superseded period(s) cancelled.`,
        result.failed > 0 ? `${result.failed} could not be cancelled.` : '',
        result.filed > 0 ? `${result.filed} already filed and left as is.` : '',
        result.skippedInProgress > 0
          ? `${result.skippedInProgress} already started and left alone.`
          : '',
      ].filter(Boolean).join(' ');
    }, 'Could not cancel the superseded periods.');
  }, [engagement, run]);

  if (!isOpen || !engagement) return null;

  const quarters = quarterOptions(engagement.financialYear);

  // The server's list when it has answered, the local four while it has not.
  // A blocked quarter stays VISIBLE and disabled rather than being dropped —
  // hiding it would leave the user wondering where Q1 went.
  const quarterChoices = electable
    ? electable.map((q) => ({
        value: inputDate(q.effectiveFromQuarter),
        label: q.label,
        isDisabled: !q.selectable,
      }))
    : quarters;

  const blockedQuarters = (electable ?? []).filter((q) => !q.selectable);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-xl">
        {busy && (
          <div
            style={{
              position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 10, borderRadius: 'inherit',
            }}
          >
            <div
              style={{
                width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
                borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
              }}
            />
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Manage GST — {engagement.clientLegalName} · {fyLabel(engagement.financialYear)}
          </h2>
          <button className="modal-close" onClick={onClose} title="Close">
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {!canEdit && (
            <div
              style={{
                border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: 12,
                fontSize: '0.8125rem', color: 'var(--text-secondary)',
              }}
            >
              You have view access. Changing scope or filing frequency needs the edit permission.
            </div>
          )}

          {/* ---- settings ------------------------------------------------ */}
          <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '0 0 8px',
                       paddingBottom: 4, borderBottom: '1px solid var(--border-color)' }}>
            Settings
          </h3>

          <div className="form-grid-2">
            <div className="form-group">
              <SyutSelect
                label="Data source"
                options={DATA_SOURCE_OPTIONS}
                value={dataSourceMode}
                isMulti={false}
                isClearable={false}
                size="sm"
                isDisabled={!canEdit}
                onChange={(v) => setDataSourceMode((v as string) || 'CLIENT_SUPPLIED')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Filing notes</label>
              <input
                type="text"
                className="form-input"
                value={filingNotes}
                disabled={!canEdit}
                placeholder="Anything the preparer should know."
                onChange={(e) => setFilingNotes(e.target.value)}
              />
              <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                Never portal credentials — the firm does not store them.
              </div>
            </div>
          </div>

          {canEdit && settingsDirty && (
            <div style={{ marginBottom: 12 }}>
              <button className="btn btn-primary" onClick={saveSettings} disabled={busy}>
                Save settings
              </button>
            </div>
          )}

          {/* ---- registrations ------------------------------------------- */}
          <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '14px 0 8px',
                       paddingBottom: 4, borderBottom: '1px solid var(--border-color)' }}>
            Registrations in scope
          </h3>

          {registrations.length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              No registrations in scope.
            </div>
          )}

          {registrations.map((r) => {
            const withdrawn = r.scopeStatus === 'WITHDRAWN';
            const editing = editingRow === r.id;

            return (
              <div
                key={r.id}
                style={{
                  border: '1px solid var(--border-color)', borderRadius: 6,
                  padding: '0.625rem 0.75rem', marginBottom: 8,
                  background: 'var(--bg-primary)', opacity: withdrawn ? 0.75 : 1,
                  borderLeft: `3px solid ${withdrawn ? '#d97706' : '#2563eb'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div>
                    <strong style={{ fontSize: '0.875rem' }}>{r.gstin}</strong>
                    {r.tradeName && (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                        {' '}· {r.tradeName}
                      </span>
                    )}
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                      {r.filingFrequency} · {r.requiredHsnDigits}-digit HSN ·{' '}
                      {r.scopeFrom ? `from ${formatDate(r.scopeFrom)}` : 'from the start of the year'}
                      {r.scopeTo ? ` to ${formatDate(r.scopeTo)}` : ''}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {withdrawn && <span className="badge badge-inactive">Withdrawn</span>}
                    {canEdit && !withdrawn && !editing && (
                      <>
                        <button
                          className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                          onClick={() => {
                            setEditingRow(r.id);
                            setRowHsn(String(r.requiredHsnDigits));
                            setRowScopeTo(inputDate(r.scopeTo));
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                          onClick={() => {
                            setCadenceTarget(r);
                            setCadenceQuarter(quarters[0].value);
                            setCadenceValue(r.filingFrequency);
                            void loadElectable(r);
                          }}
                        >
                          Change filing frequency
                        </button>
                        <button
                          className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                          onClick={() => { setWithdrawTarget(r); setWithdrawDate(''); setWithdrawReason(''); }}
                        >
                          Withdraw
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {editing && (
                  <div className="form-grid-3" style={{ marginTop: 8 }}>
                    <div className="form-group">
                      <SyutSelect
                        label="HSN digits"
                        options={HSN_OPTIONS}
                        value={rowHsn}
                        isMulti={false}
                        isClearable={false}
                        size="sm"
                        onChange={(v) => setRowHsn((v as string) || '4')}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">In scope until</label>
                      <input
                        type="date"
                        className="form-input"
                        value={rowScopeTo}
                        onChange={(e) => setRowScopeTo(e.target.value)}
                      />
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                        Leave empty for open-ended. Narrowing it strands later periods.
                      </div>
                    </div>
                    <div className="form-group" style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                      <button className="btn btn-primary" onClick={() => saveRow(r)} disabled={busy}>
                        Save
                      </button>
                      <button className="btn btn-secondary" onClick={() => setEditingRow(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* ---- reconciliation ------------------------------------------ */}
          {reconciliation && reconciliation.outOfScope.length > 0 && (
            <>
              <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, margin: '14px 0 8px',
                           paddingBottom: 4, borderBottom: '1px solid var(--border-color)' }}>
                Superseded return periods
              </h3>

              <div
                style={{
                  border: '1px solid #f59e0b', background: 'var(--bg-secondary)',
                  borderRadius: 6, padding: '0.5rem 0.75rem', marginBottom: 8,
                  fontSize: '0.8125rem', color: 'var(--text-secondary)',
                }}
              >
                Generating periods only ever ADDS, so a narrowed scope or a changed filing
                frequency leaves these behind. That is why both shapes appear on the return
                period list until these are cleared.
                {reconciliation.filed > 0 && (
                  <>
                    {' '}<strong style={{ color: 'var(--text-primary)' }}>
                      {reconciliation.filed} already filed
                    </strong>{' '}
                    and cannot be cancelled — a lodged return is not unmade by a later change.
                  </>
                )}
              </div>

              {/*
                Clearing a year of superseded monthly periods one row at a time
                is not a workflow anybody follows, so in practice they would just
                be left there. Filed ones are never in this count.
              */}
              {canEdit && reconciliation.cancellable > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.75rem', padding: '3px 10px' }}
                    onClick={() => setBulkCancelOpen(true)}
                    disabled={busy}
                  >
                    Cancel all {reconciliation.cancellable} not-started
                  </button>
                  {reconciliation.inProgress > 0 && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginLeft: 8 }}>
                      {reconciliation.inProgress} started — cancel those individually.
                    </span>
                  )}
                </div>
              )}

              {reconciliation.outOfScope.map((o) => {
                const tone = verdictTone(o.verdict);
                return (
                  <div
                    key={o.obligationId}
                    style={{
                      display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap',
                      border: '1px solid var(--border-color)', borderRadius: 6,
                      padding: '0.5rem 0.625rem', marginBottom: 6,
                      borderLeft: `3px solid ${tone.colour}`,
                    }}
                  >
                    <div>
                      <strong style={{ fontSize: '0.8125rem' }}>
                        {o.formCode} · {o.periodLabel}
                      </strong>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                        {' '}· {o.registrationNumber}
                      </span>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        {o.reason}
                        {o.filedAt && <> Filed {formatDate(o.filedAt)}.</>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <span className={tone.cls}>{tone.label}</span>
                      {canEdit && o.verdict !== 'Filed' && (
                        <button
                          className="btn btn-secondary"
                          style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                          onClick={() => setCancelTarget(o)}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Close</button>
        </div>
      </div>

      {/* ---- withdraw ---------------------------------------------------- */}
      {withdrawTarget && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Withdraw {withdrawTarget.gstin}</h2>
              <button className="modal-close" onClick={() => setWithdrawTarget(null)}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">
                  In scope until <span className="text-danger">*</span>
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={withdrawDate}
                  onChange={(e) => setWithdrawDate(e.target.value)}
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                  The last day this GSTIN was in scope. The periods it already owes stay
                  exactly as they are — a registration that left mid-year still owes the
                  months it held.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Reason</label>
                <textarea
                  className="form-textarea"
                  rows={2}
                  value={withdrawReason}
                  placeholder="Registration surrendered, mandate ended…"
                  onChange={(e) => setWithdrawReason(e.target.value)}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setWithdrawTarget(null)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={confirmWithdraw}
                disabled={busy || !withdrawDate}
              >
                Withdraw
              </button>
            </div>
          </div>
        </>
      )}

      {/* ---- cadence election -------------------------------------------- */}
      {cadenceTarget && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">Filing frequency for {cadenceTarget.gstin}</h2>
              <button className="modal-close" onClick={() => setCadenceTarget(null)}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div
                style={{
                  border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                  borderRadius: 6, padding: '0.5rem 0.625rem', marginBottom: 12,
                  fontSize: '0.75rem', color: 'var(--text-secondary)',
                }}
              >
                An ADDITION, not an edit. QRMP is elected per quarter and continues until
                changed, so the earlier election stays and keeps governing the quarters it
                governed.
              </div>
              <div className="form-group">
                <SyutSelect
                  label="Effective from"
                  options={quarterChoices}
                  value={cadenceQuarter}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setCadenceQuarter((v as string) || quarterChoices[0].value)}
                />
                {/*
                  A greyed-out quarter with no explanation reads as a bug. The
                  reason names the filed return that blocks it, which is a fact
                  about the world rather than a rule the software invented.
                */}
                {blockedQuarters.length > 0 && (
                  <div
                    style={{
                      marginTop: 6, fontSize: '0.7rem', color: 'var(--text-secondary)',
                      lineHeight: 1.45,
                    }}
                  >
                    {blockedQuarters.map((q) => (
                      <div key={q.effectiveFromQuarter} style={{ marginTop: 2 }}>
                        <strong>{q.label.split('—')[0].trim()}</strong> unavailable — {q.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <SyutSelect
                  label="Filing frequency"
                  options={CADENCE_OPTIONS}
                  value={cadenceValue}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setCadenceValue((v as string) || 'MONTHLY')}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setCadenceTarget(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmCadence} disabled={busy}>
                Record election
              </button>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog
        open={cancelTarget !== null}
        title="Cancel this obligation?"
        message={
          `${cancelTarget?.formCode} for ${cancelTarget?.periodLabel} (${cancelTarget?.registrationNumber}) `
          + 'lies outside the current scope. Cancelling ends its workflow — '
          + (cancelTarget?.verdict === 'InProgress'
            ? 'and work has already started on it, so anything prepared will be lost.'
            : 'nothing has been prepared on it yet.')
        }
        confirmLabel="Cancel obligation"
        cancelLabel="Keep it"
        variant="danger"
        loading={busy}
        loadingMessage="Cancelling…"
        onConfirm={confirmCancel}
        onCancel={() => setCancelTarget(null)}
      />

      <ConfirmDialog
        open={bulkCancelOpen}
        title="Cancel all not-started superseded periods?"
        message={
          `This ends the workflow on ${reconciliation?.cancellable ?? 0} return period(s) that `
          + 'the current scope and filing frequency no longer produce. Nothing has been '
          + 'prepared on any of them.'
          + (reconciliation?.filed
            ? ` ${reconciliation.filed} filed return(s) are left untouched.`
            : '')
          + (reconciliation?.inProgress
            ? ` ${reconciliation.inProgress} already started and are NOT included — cancel those one by one.`
            : '')
        }
        confirmLabel="Cancel them"
        cancelLabel="Keep them"
        variant="danger"
        loading={busy}
        loadingMessage="Cancelling…"
        onConfirm={confirmBulkCancel}
        onCancel={() => setBulkCancelOpen(false)}
      />
    </>
  );
};

export default GstEngagementManageModal;
